
import React, { useState } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  CreatureInstance,
  SummonInstance,
  CreatureAbility,
  MinionAbility,
  RollPlus,
  CreatureSpellAsAbility,
  // EncounterLog,
  CharacterFeature,
  TransformInstance,
  RolledValue,
  SummonOption2,
  DamageTypeOverrides,
} from "@dndModels";

import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import DandDButtonBox from "@dndComponents/DandDButtonBox";

import Roller from "@dndComponents/other/Roller";
import ToastBox from "@components/display/ToastBox";
import CharacterResourceBoxesNew from "@dndComponents/character/misc/CharacterResourceBoxes";
import ImageFromString from "@dndComponents/display/ImageFromString";
import CreatureEffect from "@dndComponents/character/spells_and_abilities/CreatureEffect";

interface Props {
  character?: Character | null;
  obj: CreatureInstance | SummonInstance | TransformInstance;
  action: CreatureAbility | MinionAbility;
  dt_overrides?: DamageTypeOverrides[];
  disabled: boolean;
  trim?: boolean;
  char_feature: CharacterFeature;
  onChange: () => void; // For slots, resources, and concentration
  onClick?: () => void;
  onSummon: (selected_option: SummonOption2) => void;
}

const common_util = CommonUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export default function CreatureActionInput({
  character = null,
  obj,
  action,
  dt_overrides = [],
  disabled,
  trim,
  char_feature,
  onChange,
  onClick,
  onSummon
}: Props) {
  const [popoverTime, setPopoverTime] = useState(0);
  const [popoverEffect, setPopoverEffect] = useState<string>("");
  const [popoverMode, setPopoverMode] = useState("");
  const [ping, setPing] = useState(false);
  const [roll, setRoll] = useState<RolledValue[]>([]);
  
  const renderCastButton = () => {
    // if (action.name.includes("Second")) {
    //   debugger;
    // }
    let name = action.name;
    const dt_override_name_change = dt_overrides.find(o => o.name_formula !== "");
    if (dt_override_name_change) {
      let new_name = dt_override_name_change.name_formula;
      if (new_name.includes("replace.")) {
        // debugger;
        // {replace.Dragon.Shadow Dragon}
        new_name = new_name.replace("{","").replace("}","");
        const pieces = new_name.split(".");
        if (pieces.length === 3) {
          new_name = name.replace(pieces[1], pieces[2]);
        }
      } else {
        new_name = new_name.replace("{base.name}", name);
      }
      name = new_name;
    }
    if (action instanceof MinionAbility || ["None",""].includes(action.consumed_resource.resource_consumed)) {
      return (
        <span className={ onClick ? "clickable" : "" }
          onClick={() => {
            if (onClick) {
              onClick();
            }
          }}>
          { name }
        </span>
      );
    } else if (action.consumed_resource.resource_consumed === "Special") {
      const ability = obj.abilities.find(o => o.true_id === char_feature.true_id);
      if (ability) {
        const used = obj.resource_holder.getSpecialResourceUsed(ability.true_id);
        const total = action.consumed_resource.special_resource_amount.base;
        const uses = action.consumed_resource.amount_consumed;
        
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              className={ onClick ? "clickable" : "" }
              onClick={() => {
                if (onClick) {
                  onClick();
                }
              }}
              size={{
                xs: 12
              }}>{name}</Grid>
            <Grid
              size={{
                xs: 4
              }}>
              <DandDButtonBox
                name="Use"
                disabled={ disabled || used + uses > total }
                onClick={() => {
                  if (obj instanceof CreatureInstance) {
                    if (action.consumed_resource.resource_consumed === "Special") {
                      const ability_true_id = char_feature.true_id;
                      const used = obj.resource_holder.getSpecialResourceUsed(ability_true_id);
                      const ability = obj.abilities.filter(o => o.true_id === ability_true_id)[0];
                      if (ability) {
                        obj.resource_holder.setSpecialResourceUsed(ability_true_id, used + uses);
                        // const log = new EncounterLog();
                        // log.field = `ability_${ability.true_id}`;
                        // log.value = used + uses;
                        onChange();
                        setPing(!ping);
                      }
                    }
                  }
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 8
              }}>
              <CharacterResourceBoxesNew 
                used={used}
                total={total}
                true_total={total}
                created={0}
                type={"Special"}
                show_type={false}
                shared={false}
                onChange={(used: number) => {
                  if (obj instanceof CreatureInstance) {
                    if (action.consumed_resource.resource_consumed === "Special") {
                      const ability_true_id = char_feature.true_id;
                      const old_used = obj.resource_holder.getSpecialResourceUsed(ability_true_id);
                      const ability = obj.abilities.filter(o => o.true_id === ability_true_id)[0];
                      if (ability) {
                        obj.resource_holder.setSpecialResourceUsed(ability_true_id, used + old_used);
                        // const log = new EncounterLog();
                        // log.field = `ability_${ability.true_id}`;
                        // log.value = used + old_used;
                        onChange();
                        setPing(!ping);
                      }
                    }
                  }
                }}
              />
            </Grid>
          </Grid>
        );
      } else {
        console.error("Something is wrong");
        console.error(obj);
        console.error(char_feature);
        return null;
      }
    } else if (action.consumed_resource.resource_consumed === "Recharge") {
      const ability = obj.abilities.find(o => o.true_id === char_feature.true_id);
      if (ability && action instanceof CreatureAbility && obj instanceof CreatureInstance) {
        const used = obj.resource_holder.getSpecialResourceUsed(ability.true_id);
        const str = ability.use_string({ obj, amount_consumed: used });
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              className={ onClick ? "clickable" : "" }
              onClick={() => {
                if (onClick) {
                  onClick();
                }
              }}
              size={{
                xs: 12
              }}>{name}</Grid>
            <Grid
              size={{
                xs: 8
              }}>
              { str === "Ready" ?
                <DandDButtonBox name={str}
                  disabled={ disabled }
                  onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                    const ability_true_id = char_feature.true_id;
                    const ability = obj.abilities.filter(o => o.true_id === ability_true_id)[0];
                    if (ability) {
                      obj.resource_holder.setSpecialResourceUsed(ability_true_id, 1);
                      // const log = new EncounterLog();
                      // log.field = `ability_${ability.true_id}`;
                      // log.value = 1;
                      onChange();
                      setPing(!ping);
                    }
                  }} 
                />
              :
                <DandDButtonBox name={str}
                  disabled={ disabled }
                  onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                    // debugger;
                    // Do a recharge check
                    const rolls = new RollPlus();
                    rolls.type = "Recharge";
                    rolls.count = 1;
                    rolls.size = action.consumed_resource.recharge_die;
                    const new_roll = char_util.rollOther({ rolls: [rolls], type: "Recharge" });
                    setPopoverMode("Recharge");
                    setRoll(new_roll);
                    setPopoverTime(common_util.get_time_number());
                    if (new_roll.length === 1 && 
                      new_roll[0].roll_values.length === 1 && 
                      new_roll[0].roll_values[0].main_values.length === 1 && 
                      action.consumed_resource.recharge_on.includes(new_roll[0].roll_values[0].main_values[0])) {
                      const ability_true_id = char_feature.true_id;
                      const ability = obj.abilities.filter(o => o.true_id === ability_true_id)[0];
                      if (ability) {
                        obj.resource_holder.setSpecialResourceUsed(ability_true_id, 0);
                        // const log = new EncounterLog();
                        // log.field = `ability_${ability.true_id}`;
                        // log.value = 0;
                        onChange();
                        setPing(!ping);
                      }
                    }
                  }} 
                />
              }
            </Grid>
            <Grid
              size={{
                xs: 4
              }}>
              <CharacterResourceBoxesNew 
                used={used}
                total={1}
                true_total={1}
                created={0}
                type={"Recharge"}
                show_type={false}
                shared={false}
                onChange={(used: number) => {
                  if (obj instanceof CreatureInstance) {
                    if (action.consumed_resource.resource_consumed === "Recharge") {
                      const ability_true_id = char_feature.true_id;
                      const ability = obj.abilities.filter(o => o.true_id === ability_true_id)[0];
                      if (ability) {
                        obj.resource_holder.setSpecialResourceUsed(ability_true_id, used);
                        // const log = new EncounterLog();
                        // log.field = `ability_${ability.true_id}`;
                        // log.value = used;
                        onChange();
                        setPing(!ping);
                      }
                    }
                  }
                }}
              />
            </Grid>
          </Grid>
        );
      } else {
        console.error("Something is wrong");
        console.error(obj);
        console.error(char_feature);
        return null;
      }
    } else if (action.consumed_resource.resource_consumed === "Legendary Action" && obj instanceof CreatureInstance) {
      const used = obj.used_legendary_actions;
      const total = obj.legendary_actions_per_round;
      const uses = action.consumed_resource.amount_consumed;
      
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            className={ onClick ? "clickable" : "" }
            onClick={() => {
              if (onClick) {
                onClick();
              }
            }}
            size={{
              xs: 12
            }}>{name}</Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <DandDButtonBox
              name="Use"
              disabled={ disabled || used + uses > total }
              onClick={() => {
                if (action.consumed_resource.resource_consumed === "Legendary Action" && obj instanceof CreatureInstance) {
                  if (obj instanceof CreatureInstance) {
                    obj.used_legendary_actions += uses;
                    // const log = new EncounterLog();
                    // log.field = "used_legendary_actions";
                    // log.value = obj.used_legendary_actions;
                    onChange();
                    setPing(!ping);
                  }
                }
              }}
            />
          </Grid>
        </Grid>
      );
    } else if (action.consumed_resource.resource_consumed !== "" && obj instanceof CreatureInstance && action instanceof CreatureAbility) {
      const ability = obj.abilities.find(o => o.true_id === char_feature.true_id);
      const resource = obj.use_resources.find(o => o.resource_id === action.consumed_resource.resource_consumed);
      if (ability && resource) {
        const used = resource.used
        const total = resource.total;
        const uses = action.consumed_resource.amount_consumed;
        // debugger;
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              className={ onClick ? "clickable" : "" }
              onClick={() => {
                if (onClick) {
                  onClick();
                }
              }}
              size={{
                xs: 12
              }}>{ name }</Grid>
            <Grid
              size={{
                xs: 4
              }}>
              <DandDButtonBox
                name="Use"
                disabled={ disabled || used + uses > total }
                onClick={() => {
                  if (obj instanceof CreatureInstance) {
                    const ability_true_id = char_feature.true_id;
                    const ability = obj.abilities.filter(o => o.true_id === ability_true_id)[0];
                    const resource = obj.use_resources.find(o => o.resource_id === action.consumed_resource.resource_consumed);
                    if (ability && resource) {
                      resource.used += uses;
                      // const log = new EncounterLog();
                      // log.field = `resources`;
                      // log.value = obj.use_resources;
                      onChange();
                      setPing(!ping);
                    }
                  }
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 8
              }}>
              <CharacterResourceBoxesNew 
                used={used}
                total={total}
                true_total={total}
                created={0}
                type={""}
                show_type={false}
                shared={false}
                onChange={(used: number) => {
                  if (obj instanceof CreatureInstance) {
                    const ability_true_id = char_feature.true_id;
                    const ability = obj.abilities.filter(o => o.true_id === ability_true_id)[0];
                    const resource = obj.use_resources.find(o => o.resource_id === action.consumed_resource.resource_consumed);
                    if (ability && resource) {
                      resource.used = used;
                      // const log = new EncounterLog();
                      // log.field = `resources`;
                      // log.value = obj.use_resources;
                      onChange();
                      setPing(!ping);
                    }
                  }
                }}
              />
            </Grid>
          </Grid>
        );
      } else {
        console.error("Something is wrong");
        console.error(obj);
        console.error(char_feature);
        console.error(action);
        return null;
      }
    } 
  }

  const renderExtras = () => {
    return (
      <ToastBox
        openTime={ popoverTime }
      >
        { renderRoll() }
      </ToastBox>
    );
  }

  const renderRoll = () => {
    let name = action.name;
    // console.log(name);
    const dt_override_name_change = dt_overrides.find(o => o.name_formula !== "");
    if (dt_override_name_change) {
      let new_name = dt_override_name_change.name_formula;
      if (new_name.includes("replace.")) {
        // debugger;
        // {replace.Dragon.Shadow Dragon}
        new_name = new_name.replace("{","").replace("}","");
        const pieces = new_name.split(".");
        if (pieces.length === 3) {
          new_name = name.replace(pieces[1], pieces[2]);
        }
      } else {
        new_name = new_name.replace("{base.name}", name);
      }
      name = new_name;
    }
    const mode = popoverMode;
    if (mode === "Attack") {
      const rolls = new RollPlus();
      rolls.type = "Attack";
      if (action instanceof CreatureAbility) {
        rolls.flat = +char_util.value(action.attack_bonus, { obj });
        return (
          <Roller 
            name={name}
            // char={obj}
            // source={null}
            rolled_values={roll} 
            type="Attack" 
          />
        );
      } else if (action instanceof CreatureSpellAsAbility) {
        rolls.flat = +action.attack_bonus;
        return (
          <Roller 
            name={name}
            // char={obj}
            // source={null}
            rolled_values={roll} 
            type="Attack" 
          />
        );
      } else {
        const summon = obj as SummonInstance;
        rolls.flat = character ? char_util.value(action.attack_bonus, { 
          obj: character, 
          slot_level: summon.slot_level,
          base_slot_level: summon.base_slot_level,
          source_id: summon.source_id
        }) : action.attack_bonus.base;
        return (
          <Roller 
            name={name}
            // char={obj}
            // source={null}
            rolled_values={roll} 
            type="Attack" 
          />
        );
      }
    } else if (mode === "Damage") {
      const effect = popoverEffect;
      if (effect !== "") {
        if (effect === "Healing" || effect === "Temp HP" || effect === "Max HP") {
          return (
            <Roller 
              name={name}
              // char={obj}
              // source={null}
              rolled_values={roll}  
              type={effect} 
            />
          );
        } else {
          // debugger;
          if (action instanceof CreatureAbility) {
            return (
              <Roller 
                name={name}
                // char={obj}
                // source={null}
                rolled_values={roll} 
                type="Damage" 
              />
            );
          } else if (action instanceof CreatureSpellAsAbility) {
            return (
              <Roller 
                name={name}
                // char={obj}
                // source={null}
                rolled_values={roll} 
                type="Damage" 
              />
            );
          } else {
            return (
              <Roller 
                name={name}
                // source={null}
                // char={obj}
                rolled_values={roll} 
                type="Damage" 
              />
            );
          }
        }
      }
    } else if (mode === "Recharge") {
      // debugger;
      return (
        <Roller 
          name="Recharge"
          // char={obj}
          // source={null}
          rolled_values={roll} 
          type="Recharge" 
        />
      );
    }
    return null;
  }

  // if (action.name.includes("Breath")) {
  //   console.log(action);
  // }
  if (action instanceof CreatureAbility) {
    const dc = char_util.value(action.dc, { obj });
    const attack_bonus = char_util.value(action.attack_bonus, { obj });

    const extra_effects = dt_overrides.flatMap(o => o.extra_effects);

    return (
      <Grid container spacing={0} direction="row">
        <Grid
          style={{ fontWeight: "bold" }}
          size={{
            xs: 4
          }}>
          { renderCastButton() }
        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 8
          }}>
          { obj instanceof CreatureInstance ? 
            <>
              { action.effects.map((effect, key) => {
                // if (action.name.includes("Jaunt")) {
                //   console.log(effect);
                // }
                return (
                  <Grid
                    key={effect.true_id}
                    size={{
                      xs: 12
                    }}>
                    <CreatureEffect 
                      creature_instance={obj} 
                      action={action}
                      effect={effect} 
                      // spell={selected_spell}
                      processing={false} 
                      disabled={disabled} 
                      shared={false} 
                      level={0} 
                      wide={false} 
                      casting_time={''} 
                      // log_it={action.name.includes("Burble")}
                      show_range={key === 0}
                      dt_overrides={dt_overrides}
                      onChange={(change_types: string[]) => {
                        throw new Error('Function not implemented.');
                      }} 
                      onCostChange={(costs: { [resource: string]: number; }) => {
                        throw new Error('Function not implemented.');
                      }} 
                      onTransform={() => {
                        throw new Error('Function not implemented.');
                      }} 
                      onSummon={onSummon}
                    />
                  </Grid>
                );
              })}
              { extra_effects.map(effect => {
                return (
                  <Grid
                    key={effect.true_id}
                    size={{
                      xs: 12
                    }}>
                    <CreatureEffect 
                      creature_instance={obj} 
                      action={action}
                      effect={effect} 
                      // spell={selected_spell}
                      processing={false} 
                      disabled={disabled} 
                      shared={false} 
                      level={0} 
                      wide={false} 
                      casting_time={''} 
                      // log_it={action.name.includes("Summon")}
                      show_range={false}
                      dt_overrides={dt_overrides}
                      onChange={(change_types: string[]) => {
                        throw new Error('Function not implemented.');
                      }} 
                      onCostChange={(costs: { [resource: string]: number; }) => {
                        throw new Error('Function not implemented.');
                      }} 
                      onTransform={() => {
                        throw new Error('Function not implemented.');
                      }} 
                      onSummon={onSummon}
                    />
                  </Grid>
                );
              })}
            </>
          : action.effects.filter(o => o.type !== "Utility").map(effect => {
            const effect_type = effect.type;
            const potence_string = action.get_potence_string(obj, effect);
            return (
              <Grid
                key={effect.true_id}
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 12
                }}>
                <Grid
                  className="centered"
                  size={{
                    xs: 6
                  }}>
                  { effect.use_attack ?
                    <DandDButtonBox
                      name={ `+${attack_bonus}` }
                      disabled={ disabled }
                      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                        debugger;
                        const rolls = new RollPlus();
                        rolls.type = "Attack";
                        if (action instanceof CreatureAbility) {
                          rolls.flat = +char_util.value(action.attack_bonus, { obj });
                          setRoll(char_util.rollD20({ char_interface: obj, rolls: [rolls], type: "Attack" }));
                        }
                        setPopoverEffect(effect.type);
                        setPopoverMode("Attack");
                        setPopoverTime(common_util.get_time_number());
                      }} 
                    />
                  : effect.attack_type === "Save" &&
                    <span>
                      { `${effect.saving_throw_ability_score} ${dc}` }
                    </span> 
                  }
                </Grid>
                <Grid
                  className="centered"
                  size={{
                    xs: 6
                  }}>
                  { effect_type === "Damage" && effect.damage_types.length < 2 ?
                    <DandDButtonBox
                      name={ potence_string }
                      style={{ fontSize: "9" }}                          
                      image={ effect.damage_types.length > 0 ? effect.damage_types[0] : "" }
                      disabled={ disabled }
                      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                        const rolls = [char_util.toRollPlus(effect.potences[0].rolls, { obj })];
                        setRoll(char_util.rollOther({ char_interface: obj, rolls, type: "Damage" }));
                        setPopoverEffect(effect.type);
                        setPopoverMode("Damage");
                        setPopoverTime(common_util.get_time_number());
                      }} 
                    />
                  : effect_type === "Damage" && effect.damage_types.length > 1 ?
                    <Grid container spacing={0} direction="row">
                      <Grid
                        size={{
                          xs: 12
                        }}>
                        <DandDButtonBox
                          name={ potence_string }
                          style={{ fontSize: "9" }}                          
                          image={ effect.damage_types[0] }
                          disabled={ disabled }
                          onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                            const rolls = [char_util.toRollPlus(effect.potences[0].rolls, { obj })];
                            setRoll(char_util.rollOther({ char_interface: obj, rolls, type: "Damage" }));
                            setPopoverEffect(effect.type);
                            setPopoverMode("Damage");
                            setPopoverTime(common_util.get_time_number());
                          }} 
                        />
                      </Grid>
                      <Grid
                        size={{
                          xs: 12
                        }}>
                        { effect.damage_types.map((dt, key) => {
                          return <span key={key}><ImageFromString image={dt} />&nbsp;</span>;
                        })}
                      </Grid>
                    </Grid>
                  : !["Control","Utility","Summon","Transform","Create Resource","None"].includes(effect_type) ?
                    <DandDButtonBox
                      name={ potence_string }
                      style={{ fontSize: "9" }} 
                      find_images
                      disabled={ disabled }
                      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                        const rolls = [char_util.toRollPlus(effect.potences[0].rolls, { obj })];
                        setRoll(char_util.rollOther({ char_interface: obj, rolls, type: "Damage" }));
                        setPopoverEffect(effect.type);
                        setPopoverMode("Damage");
                        setPopoverTime(common_util.get_time_number());
                      }} 
                    />
                  :
                    <span>
                      { effect_type }
                    </span> 
                  }
                </Grid>
              </Grid>
            );
          })}
        </Grid>
        { renderExtras() }
      </Grid>
    );
  } else {
    const summon = obj as SummonInstance;
    const attack_bonus = character ? char_util.value(action.attack_bonus, { 
      obj: character, 
      slot_level: summon.slot_level,
      base_slot_level: summon.base_slot_level,
      source_id: summon.source_id
    }) : action.attack_bonus.base;
    const dc = character ? char_util.value(action.dc, { 
      obj: character, 
      slot_level: summon.slot_level,
      base_slot_level: summon.base_slot_level,
      source_id: summon.source_id
    }) : action.dc.base;
    console.log(action.effects);
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          style={{ fontWeight: "bold" }}
          className={ onClick ? "clickable" : "" }
          onClick={() => {
            if (onClick) {
              onClick();
            }
          }}
          size={{
            xs: 4
          }}>{ action.name }</Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 8
          }}>
          { action.effects.filter(o => o.type !== "Utility").map(effect => {
            const effect_type = effect.type;
            const potence_string = action.get_potence_string(obj, effect);
            // console.log(potence_string);
            return (
              <Grid
                key={effect.true_id}
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 12
                }}>
                <Grid
                  className="centered"
                  size={{
                    xs: 6
                  }}>
                  { effect.use_attack ?
                    <DandDButtonBox
                      name={ `+${attack_bonus}` }
                      disabled={ disabled }
                      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                        debugger;
                        const rolls = new RollPlus();
                        rolls.type = "Attack";
                        if (action instanceof CreatureAbility) {
                          rolls.flat = +char_util.value(action.attack_bonus, { obj });
                          setRoll(char_util.rollD20({ char_interface: obj, rolls: [rolls], type: "Attack" }));
                        }
                        setPopoverEffect(effect.type);
                        setPopoverMode("Attack");
                        setPopoverTime(common_util.get_time_number());
                      }} 
                    />
                  : effect.attack_type === "Save" &&
                    <span>
                      { `${effect.saving_throw_ability_score} ${dc}` }
                    </span> 
                  }
                </Grid>
                <Grid
                  className="centered"
                  size={{
                    xs: 6
                  }}>
                  { effect_type === "Damage" && effect.damage_types.length < 2 ?
                    <DandDButtonBox
                      name={ potence_string }
                      style={{ fontSize: "9" }}                          
                      image={ effect.damage_types.length > 0 ? effect.damage_types[0] : "" }
                      disabled={ disabled }
                      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                        const rolls = [char_util.toRollPlus(effect.potences[0].rolls, { obj })];
                        setRoll(char_util.rollOther({ char_interface: obj, rolls, type: "Damage" }));
                        setPopoverEffect(effect.type);
                        setPopoverMode("Damage");
                        setPopoverTime(common_util.get_time_number());
                      }} 
                    />
                  : effect_type === "Damage" && effect.damage_types.length > 1 ?
                    <Grid container spacing={0} direction="row">
                      <Grid
                        size={{
                          xs: 12
                        }}>
                        <DandDButtonBox
                          name={ potence_string }
                          style={{ fontSize: "9" }}                          
                          image={ effect.damage_types[0] }
                          disabled={ disabled }
                          onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                            const rolls = [char_util.toRollPlus(effect.potences[0].rolls, { obj })];
                            setRoll(char_util.rollOther({ char_interface: obj, rolls, type: "Damage" }));
                            setPopoverEffect(effect.type);
                            setPopoverMode("Damage");
                            setPopoverTime(common_util.get_time_number());
                          }} 
                        />
                      </Grid>
                      <Grid
                        size={{
                          xs: 12
                        }}>
                        { effect.damage_types.map((dt, key) => {
                          return <span key={key}><ImageFromString image={dt} />&nbsp;</span>;
                        })}
                      </Grid>
                    </Grid>
                  : !["Control","Utility","Summon","Transform","Create Resource","None"].includes(effect_type) ?
                    <DandDButtonBox
                      name={ potence_string }
                      style={{ fontSize: "9" }} 
                      find_images
                      disabled={ disabled }
                      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                        const rolls = [char_util.toRollPlus(effect.potences[0].rolls, { obj })];
                        setRoll(char_util.rollOther({ char_interface: obj, rolls, type: "Damage" }));
                        setPopoverEffect(effect.type);
                        setPopoverMode("Damage");
                        setPopoverTime(common_util.get_time_number());
                      }} 
                    />
                  :
                    <span>
                      { effect_type }
                    </span> 
                  }
                </Grid>
              </Grid>
            );
          })}
        </Grid>
        {/* <Grid item xs={4}>
          { attack_bonus > 0 ?
            <DandDButtonBox
              name={ `+${attack_bonus}` }
              disabled={ disabled }
              onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                if (character) {
                  const rolls = new RollPlus();
                  rolls.type = "Attack";
                  rolls.flat = +char_util.value(action.attack_bonus, { 
                    obj: character, 
                    slot_level: summon.slot_level,
                    base_slot_level: summon.base_slot_level,
                    source_id: summon.source_id
                  });
                  setRoll(char_util.rollD20({ char_interface: obj, rolls: [rolls], type: "Attack" }));
                  setPopoverMode("Attack");
                  setPopoverTime(common_util.get_time_number());
                }
              }} 
            />
          : action.saving_throw_ability_score !== "" &&
            <span>
              { `${action.saving_throw_ability_score} ${dc}` }
            </span> 
          }
        </Grid>
        <Grid item xs={4}>
          { !["Control","Utility","Summon","Transform","Create Resource"].includes(action.effect.type) ?
            <DandDButtonBox
              name={ damage_string }
              image={ action.effect.type }
              disabled={ disabled }
              onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                // debugger;
                if (character) {
                  if (potence) {
                    const rolls = [char_util.toRollPlus(potence.rolls, { 
                      obj: character, 
                      slot_level: summon.slot_level,
                      base_slot_level: summon.base_slot_level,
                      source_id: summon.source_id
                    })];
                    const new_roll = char_util.rollOther({ char_interface: obj, rolls, type: "Damage" });
                    setRoll(new_roll);
                    setPopoverEffect(action.effect.type);
                    setPopoverMode("Damage");
                    setPopoverTime(common_util.get_time_number());
                  } else {
                    const rolls = [char_util.toRollPlus(action.effect.potences[0].rolls, { 
                      obj: character, 
                      slot_level: summon.slot_level,
                      base_slot_level: summon.base_slot_level,
                      source_id: summon.source_id
                    })];
                    const new_roll = char_util.rollOther({ char_interface: obj, rolls, type: "Damage" });
                    setRoll(new_roll);
                    setPopoverEffect(action.effect.type);
                    setPopoverMode("Damage");
                    setPopoverTime(common_util.get_time_number());
                  }
                }
              }} 
            />
          : action.effect.type !== "None" &&
            <span className='centered'>
              { action.effect.type }
            </span> 
          }
        </Grid> */}
        { renderExtras() }
      </Grid>
    );
  }
}
